import {logger} from 'yougile-util'
import {Backend} from './backend'

/**
 * Monaco editor wrapper.
 */
export class MonacoEditor {
  private static initialized = false
  public static editor: any

  public instance: any

  constructor(el: Element, language = 'javascript') {
    this.instance = MonacoEditor.editor.create(el, {
      value: '',
      scrollBeyondLastLine: false,
      fontSize: 13,
      verticalScrollbarSize: 7,
      horizontalScrollbarSize: 7,
      lineDecorationsWidth: 10,
      lineNumbersMinChars: 4,
      language
    })
    const model = this.instance.getModel()
    model.updateOptions({
      tabSize: 2
    })
  }

  /**
   * Need to be called before doing any rendering.
   *
   * Monaco editor adds some custom AMD loader, that defines “window.define”
   * this makes other libs (eg: canvas-to-blob, …) to switch to
   * AMD mode. To prevent this unwanted behaviour, init() should be called
   * only after all these modules were loaded.
   */
  public static init(libStr: string, cb: () => void): void {
    if (MonacoEditor.initialized) {
      return
    }
    MonacoEditor.initialized = true
    const win = window as any

    // saving node variables (for electron compatibility)
    const nodeProcess = win.process || logger.log
    const nodeModule = win.module || logger.log
    const nodeRequire = win.require || logger.log
    delete win.process
    delete win.module
    delete win.require

    // win.require = {paths: {vs: '_static/js/ext/monaco-editor/min/vs'}}

    Backend.loadScript('/monaco/vs/loader.js', () => {
      win.require.config({paths: {vs: '/monaco/vs'}})
      win.require(['vs/editor/editor.main'], () => {
        MonacoEditor.setDefaults(libStr, win.monaco)
        MonacoEditor.editor = win.monaco.editor

        // restoring node variables
        win.process = nodeProcess
        win.module = nodeModule
        win.require = nodeRequire

        cb()
      })
    })
  }

  private static setDefaults(libStr: string, monaco: any): void {
    monaco.languages.typescript.javascriptDefaults.setCompilerOptions({
      target: monaco.languages.typescript.ScriptTarget.ES5,
      allowNonTsExtensions: true,
      tabSize: 2,
      noLib: false
    })
    monaco.languages.typescript.javascriptDefaults.setDiagnosticsOptions({
      noSemanticValidation: true,
      noSyntaxValidation: false
    })
    monaco.languages.typescript.javascriptDefaults.addExtraLib(
      libStr,
      'yougile.d.ts'
    )
  }

  public open(): void {
    this.instance.layout()
    this.instance.focus()
  }

  public getValue(): string {
    return this.instance.getValue()
  }

  public setValue(code: string): void {
    this.instance.setValue(code)
  }

  public getCurrentLine(): number {
    return this.instance.getPosition().lineNumber
  }

  public getCurrentColumn(): number {
    return this.instance.getPosition().column
  }

  public setPosition(lineNumber: number, column: number): void {
    this.instance.setPosition({column, lineNumber})
  }

  public insertAtCursor(code: string): void {
    const line = this.getCurrentLine()
    const value = this.getValue()
    const lines = value.split('\n')
    lines.splice(line, 0, code)
    this.setValue(lines.join('\n'))
    this.open()
    this.setPosition(line + 1, 0)
  }
}
